import React from "react";
import {
  Input,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import { parsePhoneNumberWithError, AsYouType } from "libphonenumber-js";

/* global HTMLInputElement */

const useStyles = makeStyles({
  input: {
    width: "100%",
    minWidth: "0",
    "&::placeholder": {
      color: tokens.colorNeutralForeground4,
    },
  },
  invalid: {
    ...shorthands.borderColor(tokens.colorPaletteRedBorder2),
  },
});

interface PhoneFieldProps {
  value: string;
  placeholder?: string;
  required?: boolean;
  onChange?: (value: string) => void;
}

const PhoneField: React.FC<PhoneFieldProps> = ({
  value,
  placeholder = "(555) 555-5555",
  required = false,
  onChange,
}) => {
  const styles = useStyles();

  const formatPhoneNumber = (input: string) => {
    const asYouType = new AsYouType("US");
    return asYouType.input(input);
  };

  const isValidNumber = (phone: string) => {
    try {
      if (!phone) return true;
      const parsed = parsePhoneNumberWithError(phone, "US");
      return parsed.isValid();
    } catch {
      return false;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    onChange?.(formatted);
  };

  const isValid = isValidNumber(value);

  return (
    <Input
      type="tel"
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      required={required}
      className={mergeClasses(
        styles.input,
        isValid ? undefined : styles.invalid,
      )}
      aria-invalid={!isValid}
    />
  );
};

export default PhoneField;
